<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { page } from '$app/state';
	import CheckmarkCircled from '$lib/icons/checkmarkCircled.svelte';
	import ImageBlock from '../imageBlock.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import type { SbImage, SbLink, StripeProductData } from '$lib/storyblok/types';
	import Image from '$lib/storyblok/components/image.svelte';
	import CheckmarkCircledOutline from '$lib/icons/checkmarkCircledOutline.svelte';
	import ProductPrice from '$lib/storyblok/parts/localOffer/productPrice.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			default_headline: string;
			text: ISbRichtext;
			zip_code_label: string;
			card_image: Array<ISbStoryData<ImageBlock>>;
			logo_image: SbImage;
			card_headline: string;
			checklist: Array<{ text: string }>;
			card_label?: string;
			bundle_id?: string;
			stripe_product: StripeProductData;
			primary_cta_label: string;
			secondary_cta_label?: string;
			secondary_cta_target?: SbLink;
			price_suffix: string;
			price_subline: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let zip = $derived(page.url.searchParams.get('zip'));
	let city = $derived(page.url.searchParams.get('city') || 'your city');
</script>

<section
	id="hero"
	use:editable={content}
	class="hero-bg-light min-h-screen pb-6 pt-8 transition-padding duration-300 ease-out lg:flex lg:items-center"
	style="padding-top: var(--full-header-height)"
>
	<PageGrid16 margin>
		<div
			class="col-span-full flex flex-col gap-y-3 text-brown-1 max-lg:pt-8 lg:col-start-2 lg:col-end-8 lg:gap-y-5"
		>
			<h1
				class="text-balance font-rhymes text-[44px]/[110%] tracking-[-.44px] lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				{#if zip && city}
					{content.headline.replace('[city]', city)}
				{:else}
					{content.default_headline}
				{/if}
			</h1>
			<div class="text-balance text-[14px]/[160%] lg:text-[16px]/[150%]">
				<SbRichtext text={content.text} />
			</div>
		</div>
		{#if zip}
			<div
				class="col-span-full mt-5 flex items-center justify-between rounded-2xl bg-green-6 py-[14px] pl-5 pr-[18px] text-green-1 lg:col-start-2 lg:col-end-7"
			>
				<div class="flex flex-col gap-y-[10px]">
					<span class="text-[12px]/[100%]">{content.zip_code_label}</span>
					<p class="text-[16px]/[100%] tracking-[-.16px]">{zip}, {city}</p>
				</div>
				<CheckmarkCircled class="h-6 w-6" />
			</div>
		{/if}
	</PageGrid16>
	<section
		class="relative px-5 py-6 max-lg:mt-4 max-lg:bg-brown-6 lg:absolute lg:inset-y-0 lg:right-0 lg:flex lg:w-1/2 lg:items-center lg:justify-center"
	>
		<div
			class="absolute inset-y-0 py-5 pr-5 transition-all duration-300 ease-out max-lg:hidden"
			style="top: var(--full-header-height)"
		>
			{#each content.card_image as image}
				<Blok content={{ ...image, rounded_corners: '2xl' }} />
			{/each}
		</div>
		<div class="offer-card overflow-hidden rounded-xl bg-white text-brown-1">
			<div class="mb-5 aspect-2/1 w-full lg:hidden">
				{#each content.card_image as image}
					<Blok content={{ ...image, rounded_corners: 'none' }} />
				{/each}
			</div>
			<div class="flex flex-col items-start px-6 pb-6 pt-5">
				<Image
					class="mb-6 ml-1"
					src={content.logo_image.filename}
					alt={content.logo_image.alt}
					title={content.logo_image.title}
				/>
				<h1
					class="mb-5 text-[24px]/[100%] tracking-[-.24px] lg:text-[32px]/[110%] lg:tracking-[-.32px]"
				>
					{content.card_headline}
				</h1>

				<ul class="flex flex-col gap-y-3 text-[14px]/[160%] lg:text-[16px]/[150%]">
					{#each content.checklist as item}
						<li class="inline-flex items-center gap-x-3 lg:gap-x-2">
							<CheckmarkCircledOutline class="h-4 w-4" />
							{item.text}
						</li>
					{/each}
				</ul>

				<hr class="my-6 h-[1px] w-full bg-brown-5 lg:my-7 lg:bg-brown-6" />

				{#if content.card_label && content.card_label.length > 0}
					<div
						class="mb-2 rounded-[4px] bg-[#FFF0E4] px-2 py-[6px] text-[12px]/[100%] font-semibold uppercase tracking-[-.24px] text-orange-3 lg:mb-3"
					>
						{content.card_label}
					</div>
				{/if}

				<ProductPrice
					class="text-[20px]/[110%] tracking-[-.2px]"
					price={content.stripe_product.data.price.unit_amount || 99900}
					promoCode={content.stripe_product.data.promotionCode}
					interval={content.stripe_product.data.price.recurring?.interval}
					suffix={content.price_suffix}
					subline={content.price_subline}
				/>

				<div class="mt-5 flex items-center gap-x-2 lg:mt-7">
					<Button
						onclick={() => {
							checkoutSession.select(content.stripe_product, {
								productBundle: content.bundle_id
							});
							shoppingCart.open();
						}}>{content.primary_cta_label}</Button
					>
					{#if content.secondary_cta_label && content.secondary_cta_label.length > 0 && content.secondary_cta_target}
						<Button
							class="btn-tertiary btn-small btn-white"
							style="--hover-opacity: 0"
							href={getSbUrl(content.secondary_cta_target)}
							target={content.secondary_cta_target.target}>{content.secondary_cta_label}</Button
						>
					{/if}
				</div>
			</div>
		</div>
	</section>
</section>

<style lang="scss">
	div.offer-card {
		border: 1px solid rgba(233, 231, 230, 0);
		box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.04);
	}
</style>
